/**
 * Modal — the canonical dialog for the app.
 *
 * Features: focus-trap (Tab cycles inside, focus restored on close), body
 * scroll-lock, Escape + backdrop close, `role="dialog"` / `aria-modal`, size
 * presets, and a header (title) + footer slot. Token-themed and mobile
 * friendly — a full-width bottom sheet on small screens, a centred panel from
 * `sm` up. Rendered through a portal on `document.body`.
 *
 *   <Modal title="Nouveau contact" onClose={close} footer={…}>…</Modal>
 *
 * Close-control contract: the header renders the single, canonical close
 * affordance (the "×", `aria-label="Fermer"`). The Modal never auto-injects a
 * footer close. The `footer` slot is for *actions* (e.g. Annuler + Enregistrer)
 * — do NOT add a bare "Fermer" button there, as it duplicates the header ×.
 */

import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Module-level modal stack. Every open Modal pushes a token on mount and pops it
 * on unmount, so nested modals (a Modal opened from inside another Modal) layer
 * correctly:
 *   - only the TOP modal handles Escape and traps focus (outer modals no-op),
 *   - z-index grows with depth so each backdrop sits above the one below,
 *   - body scroll-lock is ref-counted (locked while ≥1 modal is open).
 * A module singleton (not React context) because modals render through portals
 * on document.body and must coordinate across unrelated subtrees.
 */
const modalStack: symbol[] = [];
let lockedScrollPrev: string | null = null;

function pushModal(token: symbol): void {
  modalStack.push(token);
  if (modalStack.length === 1) {
    lockedScrollPrev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
}

function popModal(token: symbol): void {
  const i = modalStack.lastIndexOf(token);
  if (i !== -1) modalStack.splice(i, 1);
  if (modalStack.length === 0 && lockedScrollPrev !== null) {
    document.body.style.overflow = lockedScrollPrev;
    lockedScrollPrev = null;
  }
}

const isTopModal = (token: symbol): boolean =>
  modalStack.length > 0 && modalStack[modalStack.length - 1] === token;

/** Base z-index; each nesting level adds 20 so a nested backdrop sits above the
 *  panel below it. */
const Z_BASE = 10000;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Keep in step with the exit-animation durations in index.css
// (.um-animate-panel-out / .um-animate-backdrop-out; mobile sheet is longest).
const EXIT_MS = 200;

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'wide';

const SIZE: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  wide: 'sm:max-w-5xl',
};

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  /** Text title rendered in the header (also used as the accessible name). */
  title?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Actions row (e.g. Annuler + Enregistrer). Not a place for a "Fermer"
   * close button — the header × is the canonical close. */
  footer?: ReactNode;
  /** Custom header content; overrides `title` when provided. */
  header?: ReactNode;
  size?: ModalSize;
  /** Click on the backdrop closes the dialog. Defaults to true. */
  closeOnBackdrop?: boolean;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  header,
  size = 'md',
  closeOnBackdrop = true,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Stable identity + nesting depth for the module-level modal stack. Depth is
  // captured at mount (= how many modals were already open) and stays fixed
  // while this one is open, since modals close top-down (LIFO).
  const tokenRef = useRef<symbol>();
  if (!tokenRef.current) tokenRef.current = Symbol('modal');
  const [depth] = useState(() => modalStack.length);

  // Exit animation: play a fade/scale-out, then unmount via the real `onClose`.
  // A ref mirrors the flag so the (stable) key handler can short-circuit.
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    if (prefersReducedMotion()) {
      // Still animate a soft fade (index.css keeps a transform-free fade under
      // reduced motion), just on a shorter timer.
      closingRef.current = true;
      setClosing(true);
      window.setTimeout(onClose, 120);
      return;
    }
    closingRef.current = true;
    setClosing(true);
    window.setTimeout(onClose, EXIT_MS);
  }, [onClose]);

  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    const token = tokenRef.current!;
    pushModal(token); // ref-counts the body scroll-lock across nested modals
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;

    const focusables = (): HTMLElement[] =>
      panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];

    // Move focus into the dialog.
    (focusables()[0] ?? panel)?.focus();

    const onKey = (event: KeyboardEvent) => {
      // Only the TOP-most modal reacts. Every open modal has a document-level
      // (capture) handler; the ones below no-op so Escape closes just the top
      // dialog and Tab stays trapped inside it — the core of nested modals.
      if (!isTopModal(token)) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      popModal(token); // releases the scroll-lock only when the last modal closes
      previouslyFocused?.focus?.();
    };
    // Mount/unmount only — Escape close routes through a stable ref so a
    // changing `onClose` identity never re-locks scroll or re-steals focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (typeof document === 'undefined') return null;

  const hasStringTitle = typeof title === 'string';
  const showHeader = header != null || title != null;

  return createPortal(
    <div
      style={{ zIndex: Z_BASE + depth * 20 }}
      className={`fixed inset-0 flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-md sm:items-center sm:p-4 ${
        closing ? 'um-animate-backdrop-out' : 'um-animate-backdrop'
      }`}
      onClick={closeOnBackdrop ? requestClose : undefined}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasStringTitle ? titleId : undefined}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        // 3D raised border: deep outer elevation + a light top inner highlight and
        // a dark bottom inner line, so the panel reads as beveled/lifted off the
        // backdrop. Theme-agnostic (rgba white/black inset lines work on any bg).
        className={`um-modal-3d flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-t-token-xl border border-border bg-surface text-foreground outline-none ring-1 ring-black/10 dark:ring-white/10 sm:max-h-[88vh] sm:rounded-token-lg ${
          closing ? 'um-animate-panel-out' : 'um-animate-panel'
        } ${SIZE[size]}`}
      >
        {showHeader && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/[0.12] via-accent/[0.06] to-transparent px-5 py-3.5">
            {header ?? (
              <h2 id={titleId} className="truncate text-base font-semibold text-foreground">
                {title}
              </h2>
            )}
            <button
              type="button"
              onClick={requestClose}
              aria-label="Fermer"
              className="-mr-1 shrink-0 rounded-token-sm p-1.5 text-muted transition hover:bg-primary/10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
        {footer != null && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border bg-surface-2/60 px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
